(() => {
  'use strict';

  if (window.productVariantCardsInitialized) return;
  window.productVariantCardsInitialized = true;

  const FORM_SELECTOR = '[data-product-variant-card-form]';
  const VARIANT_INPUT_SELECTOR = '[data-variant-card-input]';

  const dispatchChange = (control) => {
    control.dispatchEvent(new Event('input', { bubbles: true }));
    control.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const parseOptions = (input) => {
    try {
      return JSON.parse(input.dataset.variantOptions || '[]');
    } catch (error) {
      return [];
    }
  };

  const syncOriginalControls = (form, variantInput) => {
    const wrapper = form.querySelector('[data-original-option-selectors]');
    if (!wrapper) return;

    const selectedOptions = parseOptions(variantInput);
    const selects = Array.from(wrapper.querySelectorAll('select'));

    selects.forEach((select, index) => {
      const value = selectedOptions[index];
      if (
        value != null &&
        Array.from(select.options).some((option) => option.value === value)
      ) {
        select.value = value;
        dispatchChange(select);
      }
    });

    const optionInputs = Array.from(
      wrapper.querySelectorAll('input[type="radio"], input[type="checkbox"]')
    );

    selectedOptions.forEach((value) => {
      const matchingInput = optionInputs.find((input) => input.value === value);
      if (matchingInput && !matchingInput.checked) {
        matchingInput.checked = true;
        dispatchChange(matchingInput);
      }
    });
  };

  const updateAddButton = (form, available) => {
    const submitButton = form.querySelector(
      'button[name="add"], input[name="add"], [data-add-to-cart], .btn-add-to-cart'
    );

    if (!submitButton) return;

    const addLabel = form.dataset.addLabel || 'Add to cart';
    const soldOutLabel = form.dataset.soldOutLabel || 'Sold out';

    submitButton.disabled = !available;

    if (submitButton.tagName === 'INPUT') {
      submitButton.value = available ? addLabel : soldOutLabel;
      return;
    }

    const textElement =
      submitButton.querySelector('[data-add-to-cart-text], span') || submitButton;

    textElement.textContent = available ? addLabel : soldOutLabel;
  };

  const getProductDiscountState = (form) => {
    const quantityInput = form.querySelector(
      'input[name="quantity"], [data-product-quantity] input, .quantity input'
    );

    const quantity = Math.max(
      1,
      Number.parseInt(quantityInput?.value || '1', 10) || 1
    );

    const enabled =
      form.dataset.productDiscountEnabled === 'true';

    const percentage = Number(
      form.dataset.productDiscountPercentage || 0
    );

    const minimumQuantity = Math.max(
      1,
      Number(form.dataset.productDiscountMinQuantity || 1)
    );

    return {
      enabled,
      percentage,
      minimumQuantity,
      quantity,
      active:
        enabled &&
        percentage > 0 &&
        quantity >= minimumQuantity
    };
  };

  const updateMainProductPrice = (form, variantInput) => {
    const section = form.closest('[data-section-type="product"]');
    const price = section?.querySelector(
      '[data-product-backend-discount-price]'
    );

    if (!price) return;

    const discountState = getProductDiscountState(form);
    const basePrice =
      variantInput.dataset.variantBasePrice ||
      variantInput.dataset.variantPrice ||
      '';

    const discountedPrice =
      variantInput.dataset.variantDiscountedPrice ||
      basePrice;

    const label = price.querySelector(
      '[data-product-discount-label]'
    );

    const originalPrice = price.querySelector(
      '[data-product-original-price]'
    );

    const currentPrice = price.querySelector(
      '[data-product-current-price]'
    );

    const minimumMessage = price.querySelector(
      '[data-product-discount-minimum-message]'
    );

    price.classList.toggle(
      'compare',
      discountState.active
    );

    if (label) {
      label.textContent = `${discountState.percentage}% OFF`;
      label.hidden = !discountState.active;
    }

    if (originalPrice) {
      originalPrice.textContent = discountState.active
        ? basePrice
        : '';

      originalPrice.hidden = !discountState.active;
    }

    if (currentPrice) {
      currentPrice.textContent = discountState.active
        ? discountedPrice
        : basePrice;
    }

    if (minimumMessage) {
      const showMinimumMessage =
        discountState.enabled &&
        discountState.percentage > 0 &&
        !discountState.active;

      minimumMessage.textContent =
        `Buy ${discountState.minimumQuantity} or more to get ` +
        `${discountState.percentage}% off.`;

      minimumMessage.hidden = !showMinimumMessage;
    }
  };

  const updateVariantCardPrices = (form) => {
    const discountState = getProductDiscountState(form);

    form
      .querySelectorAll(VARIANT_INPUT_SELECTOR)
      .forEach((input) => {
        const card = input.closest('.product-variant-card');
        if (!card) return;

        const basePrice =
          input.dataset.variantBasePrice ||
          input.dataset.variantPrice ||
          '';

        const discountedPrice =
          input.dataset.variantDiscountedPrice ||
          basePrice;

        const discountLabel = card.querySelector(
          '[data-variant-card-discount]'
        );

        const originalPrice = card.querySelector(
          '[data-variant-card-original-price]'
        );

        const currentPrice = card.querySelector(
          '[data-variant-card-current-price]'
        );

        if (discountLabel) {
          discountLabel.textContent =
            `${discountState.percentage}% OFF`;

          discountLabel.hidden = !discountState.active;
        }

        if (originalPrice) {
          originalPrice.textContent = discountState.active
            ? basePrice
            : '';

          originalPrice.hidden = !discountState.active;
        }

        if (currentPrice) {
          currentPrice.textContent = discountState.active
            ? discountedPrice
            : basePrice;
        }
      });
  };

  const hideLegacyProductPrice = (form) => {
    const layout = form.closest('.product-layout-form');
    const newPrice = layout?.querySelector(
      '[data-product-backend-discount-price]'
    );

    if (!layout || !newPrice) return;

    Array.from(layout.children).forEach((child) => {
      if (child === newPrice || child === form) return;

      if (
        child.matches?.(
          '.product-price-wrapper, .product-price-range'
        )
      ) {
        child.hidden = true;
        child.setAttribute('aria-hidden', 'true');
      }

      child
        .querySelectorAll?.(
          ':scope > .product-price-wrapper, :scope > .product-price-range'
        )
        .forEach((legacyPrice) => {
          if (legacyPrice === newPrice) return;
          legacyPrice.hidden = true;
          legacyPrice.setAttribute('aria-hidden', 'true');
        });
    });
  };

  const updateMainImageFallback = (form, variantInput) => {
    const imageUrl = variantInput.dataset.variantImage;
    if (!imageUrl) return;

    const section = form.closest('[data-section-type="product"]');
    const image = section?.querySelector(
      '[data-product-main-image], .product-main-image img, .product-images img, [data-product-image] img'
    );

    if (!image || image.src === imageUrl) return;

    image.src = imageUrl;
    image.removeAttribute('srcset');
  };

  const applyVariantSelection = (variantInput) => {
    const form = variantInput.closest(FORM_SELECTOR);
    if (!form) return;

    const variantId = variantInput.dataset.variantId;
    const available = variantInput.dataset.variantAvailable === 'true';
    const hiddenVariantInput = form.querySelector('[data-selected-variant]');

    if (hiddenVariantInput) {
      hiddenVariantInput.value = variantId;
      dispatchChange(hiddenVariantInput);
    }

    form.querySelectorAll('.product-variant-card').forEach((card) => {
      const input = card.querySelector(VARIANT_INPUT_SELECTOR);
      card.classList.toggle('is-selected', Boolean(input?.checked));
    });

    syncOriginalControls(form, variantInput);

    window.requestAnimationFrame(() => {
      if (hiddenVariantInput) {
        hiddenVariantInput.value = variantId;
        dispatchChange(hiddenVariantInput);
      }
    });

    updateAddButton(form, available);
    updateMainProductPrice(form, variantInput);
    updateVariantCardPrices(form);
    hideLegacyProductPrice(form);
    updateMainImageFallback(form, variantInput);

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', nextUrl.toString());

    document.dispatchEvent(
      new CustomEvent('product:variant-change', {
        bubbles: true,
        detail: {
          variantId: Number(variantId),
          available,
          source: 'variant-card-picker'
        }
      })
    );
  };

  document.addEventListener('change', (event) => {
    const input = event.target.closest(VARIANT_INPUT_SELECTOR);
    if (input) applyVariantSelection(input);
  });

  const updatePricesFromQuantity = (quantityInput) => {
    const form = quantityInput.closest(FORM_SELECTOR);
    if (!form) return;

    const selectedVariant = form.querySelector(
      `${VARIANT_INPUT_SELECTOR}:checked`
    );

    if (!selectedVariant) return;

    updateMainProductPrice(form, selectedVariant);
    updateVariantCardPrices(form);
  };

  document.addEventListener('input', (event) => {
    const quantityInput = event.target.closest(
      `${FORM_SELECTOR} input[name="quantity"]`
    );

    if (!quantityInput) return;
    updatePricesFromQuantity(quantityInput);
  });

  document.addEventListener('change', (event) => {
    const quantityInput = event.target.closest(
      `${FORM_SELECTOR} input[name="quantity"]`
    );

    if (!quantityInput) return;
    updatePricesFromQuantity(quantityInput);
  });

  const getCrossSellVariantId = (card) => {
    const select = card.querySelector('[data-cross-sell-variant]');
    if (select) return Number(select.value);

    return Number(
      card.querySelector('[data-cross-sell-single-variant]')?.dataset.variantId || 0
    );
  };

  const setCrossSellMessage = (card, message = '', isError = false) => {
    const element = card.querySelector('[data-cross-sell-message]');
    if (!element) return;

    element.textContent = message;
    element.hidden = !message;
    element.classList.toggle('is-error', isError);
  };

  const updateCrossSellPrice = (select) => {
    const card = select.closest('[data-product-cross-sell-card]');
    const section = select.closest('[data-product-cross-sells]');
    const selectedOption = select.selectedOptions?.[0];

    if (!card || !section || !selectedOption) return;

    const discountEnabled = section.dataset.discountEnabled === 'true';
    const percentage = Number(section.dataset.discountPercentage || 0);
    const discountActive = discountEnabled && percentage > 0;

    const originalPrice = card.querySelector(
      '.product-cross-sell-card__original-price'
    );
    const currentPrice = card.querySelector(
      '.product-cross-sell-card__current-price'
    );
    const discountLabel = card.querySelector(
      '.product-cross-sell-card__discount'
    );

    if (originalPrice) {
      originalPrice.textContent = discountActive
        ? selectedOption.dataset.basePrice || ''
        : '';
      originalPrice.hidden = !discountActive;
    }

    if (currentPrice) {
      currentPrice.textContent = discountActive
        ? selectedOption.dataset.discountedPrice || ''
        : selectedOption.dataset.basePrice || '';
    }

    if (discountLabel) discountLabel.hidden = !discountActive;
  };

  const fallbackAjaxAdd = async (variantId) => {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }]
      })
    });

    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(
        result.description || result.message || 'The product could not be added.'
      );
    }

    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { source: 'product-cross-sell', item: result }
      })
    );

    const trigger = document.querySelector(
      '[data-cart-drawer-trigger], [data-cart-toggle], .js-drawer-open-cart, [aria-controls="CartDrawer"], [aria-controls="cart-drawer"]'
    );
    trigger?.click();

    return result;
  };

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-cross-sell-variant]');
    if (select) updateCrossSellPrice(select);
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cross-sell-add]');
    if (!button) return;

    event.preventDefault();

    const card = button.closest('[data-product-cross-sell-card]');
    if (!card || button.disabled) return;

    const variantId = getCrossSellVariantId(card);
    const originalText = button.textContent.trim();

    if (!variantId) {
      setCrossSellMessage(card, 'Please select an available option.', true);
      return;
    }

    button.disabled = true;
    button.textContent = 'ADDING…';
    setCrossSellMessage(card);

    try {
      if (typeof window.blubolt?.addToCart === 'function') {
        await window.blubolt.addToCart({ id: variantId, quantity: 1 }, true);
      } else {
        await fallbackAjaxAdd(variantId);
      }

      button.textContent = 'ADDED';
      setCrossSellMessage(card, 'Added to cart.');

      window.setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        setCrossSellMessage(card);
      }, 1400);
    } catch (error) {
      console.error('Cross-sell add error:', error);
      button.textContent = originalText;
      button.disabled = false;
      setCrossSellMessage(
        card,
        error?.message || 'The product could not be added.',
        true
      );
    }
  });

  const initialize = () => {
    document.querySelectorAll(FORM_SELECTOR).forEach((form) => {
      const checkedInput = form.querySelector(`${VARIANT_INPUT_SELECTOR}:checked`);
      if (checkedInput) applyVariantSelection(checkedInput);
    });
  };

  document.addEventListener('DOMContentLoaded', initialize);
  initialize();
})();
